import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { PredictionDriftController } from './prediction-drift.controller';
import { PredictionMode } from './prediction-drift.types';
import * as turf from '@turf/turf';

@Component({
  selector: 'app-prediction-drift',
  templateUrl: './prediction-drift.component.html',
  styleUrls: ['./prediction-drift.component.scss'],
})
export class PredictionDriftComponent implements OnChanges, OnDestroy {
  @Input() controller!: PredictionDriftController;
  @Input() sensor: any;
  @Input() sourceLayer: any;

  get mode(): PredictionMode { return this.controller.predictionMode; }
  get modeChosen(): boolean { return this.controller.modeChosen; }
  get selectionEnabled(): boolean { return this.controller.selectionEnabled; }
  get selectedPolygons() { return this.controller.selectedPredictionPolygons; }
  get allDetectionsCount(): number { return this.controller.allDetectionsCount; }
  get cost(): number { return this.controller.predictionCost; }
  get weatherCost(): number { return this.controller.predictionWeatherCost; }
  get feedCost(): number { return this.controller.predictionFeedCost; }
  get weatherPoint(): number { return this.controller.predictionWeatherPoint; }
  get feedPoint(): number { return this.controller.predictionFeedPoint; }
  get totalPoint(): number { return this.controller.predictionPointTotal; }
  get costCurrency(): string { return this.controller.predictionCostCurrency; }
  get feedName(): string { return this.controller.predictionFeedName; }
  get costLoading(): boolean { return this.controller.predictionCostLoading; }
  get costError(): string { return this.controller.predictionCostError; }
  get isPointCustomer(): boolean { return this.controller.isPointCustomer; }
  get retryableCostError(): boolean {
    return this.costError === 'Unable to retrieve the latest cost. Please try again.';
  }
  get costErrorMessage(): string {
    return this.retryableCostError
      ? 'Unable to retrieve the latest cost.'
      : this.costError;
  }
  get paymentCompleted(): boolean { return this.controller.paymentCompleted; }
  get predictionStarted(): boolean { return this.controller.predictionStarted; }

  get showSelectionList(): boolean {
    return this.modeChosen && this.mode === 'select' && this.selectedPolygons.length > 0;
  }

  get selectedCount(): number {
    return this.selectedPolygons.length;
  }

  get showEmptySelectHint(): boolean {
    return this.modeChosen && this.mode === 'select' && !this.paymentCompleted && this.selectedPolygons.length === 0;
  }

  get showCostSection(): boolean {
    if (!this.modeChosen || this.paymentCompleted) return false;
    return (this.mode === 'select' && this.selectedPolygons.length > 0) ||
           (this.mode === 'all' && this.allDetectionsCount > 0);
  }

  get paymentRedirecting(): boolean {
    return this.controller.paymentRedirecting;
  }

  get paymentDisabled(): boolean {
    return !this.modeChosen || this.paymentRedirecting || this.costLoading || !!this.costError || this.controller.predictionChargeAmount <= 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourceLayer'] && this.controller && this.sourceLayer) {
      this.controller.registerPredictionDetectionLayer(this.sourceLayer);
    }
  }

  onModeChange(modeValue: string): void {
    this.controller.onModeChange(modeValue as PredictionMode);
  }

  removeDetection(id: string): void {
    this.controller.removeDetection(id);
  }

  clearAllDetections(): void {
    this.controller.clearSelectedDetections();
  }

  proceedToPayment(): void {
    this.controller.openPaymentDialog();
  }

  retryCost(): void {
    this.controller.retryPredictionCost();
  }

  formatPoints(value: number): string {
    const pointValue = Number(value);
    const formattedValue = Number.isFinite(pointValue) ? pointValue.toLocaleString('en-US') : '0';
    return `${formattedValue} Points`;
  }

  getDetectionLabel(index: number): string {
    return `Polygon ${index + 1}`;
  }

  getDetectionArea(detection: any): string {
  if (!detection?.geometry) {
    return '';
  }

  try {
    const areaSqMeters = turf.area(detection.geometry);

    // Round to nearest whole number
    return `${Math.round(areaSqMeters)} sqm`;
  } catch (error) {
    return '';
  }
  }

  ngOnDestroy(): void {
    if (this.controller?.activeSensor === this.sensor) {
      this.controller.deactivate();
    }
  }
}
